// The card controls' override BRIDGE (#6753, review thread 4436681010): what a door's answer
// said about a job or an epic (a pause requested, a resume, an epic's pause mark or model)
// holds only from the answer to the next fleet read that STARTED after it. That read is
// authoritative and wins, whatever it shows: another client may have changed the work since,
// so an override that waited for the read to "agree" could outlive a newer opposite state for
// ever. A read that started BEFORE the answer is older than it, and the override is kept.
//
// The clock is the browser's on both sides: an override is stamped when its answer lands, a
// read when its fetch starts (`useFleetUtilization` stamps `read_started_ms`, the same
// fetch-start rule the live layer's `ready` re-read uses, `live/live-query.ts`). The REST read
// carries no server revision to compare instead. One helper for the job overrides
// (`./card-controls.ts`) and the epic ones (`./epic-controls.ts`).

/** One override and when its answer landed. */
export type Bridged<V> = { readonly value: V; readonly at: number };

export const bridge = <V>(value: V, at: number): Bridged<V> => ({ value, at });

/**
 * The overrides a read that started at `readStartedAt` leaves standing: those whose answer
 * landed after that read began. The same map back when none is dropped, so no signal fires.
 */
export function settleBridges<K, V>(
  overrides: ReadonlyMap<K, Bridged<V>>,
  readStartedAt: number,
): ReadonlyMap<K, Bridged<V>> {
  let next: Map<K, Bridged<V>> | null = null;
  for (const [key, b] of overrides) {
    if (readStartedAt < b.at) continue;
    next ??= new Map(overrides);
    next.delete(key);
  }
  return next ?? overrides;
}
